import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { TitleBar } from '@resistapp/client/components/bars/title-bar';
import { theme } from '@resistapp/client/components/shared/theme';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';

function ShipmentTypeCard({
  to,
  title,
  description,
  disabled,
  disabledLabel,
}: {
  to: string;
  title: string;
  description: string;
  disabled?: boolean;
  disabledLabel?: string;
}) {
  const shared = {
    borderWidth: '1px' as const,
    borderRadius: 6,
    padding: theme.spacing[4],
    maxW: 280,
    w: '100%',
  };

  if (disabled) {
    return (
      <Box {...shared} borderColor="gray.200" bg="gray.50" opacity={0.7} cursor="not-allowed">
        <HStack spacing={2} mb={1}>
          <Text fontWeight={600} color="gray.400">
            {title}
          </Text>
          {disabledLabel ? (
            <Badge colorScheme="gray" fontSize="xs">
              {disabledLabel}
            </Badge>
          ) : null}
        </HStack>
        <Text fontSize="sm" color="gray.400" mt={1}>
          {description}
        </Text>
      </Box>
    );
  }

  return (
    <Box
      as={RouterLink}
      to={to}
      {...shared}
      borderColor="gray.200"
      cursor="pointer"
      _hover={{ borderColor: theme.colors.blue500, boxShadow: 'md' }}
      transition="border-color 0.15s, box-shadow 0.15s"
      textDecoration="none"
    >
      <Text fontWeight={600} color={theme.colors.neutral900}>
        {title}
      </Text>
      <Text fontSize="sm" color="gray.500" mt={1}>
        {description}
      </Text>
    </Box>
  );
}

export function AdminShipmentRequestTypeView() {
  return <ShipmentRequestTypeView dnaPath="/admin/shipment/dna" environmentalPath="/admin/shipment/raw" />;
}

export function WorkbookShipmentRequestTypeView() {
  const { workbookId = '' } = useParams<{ workbookId: string }>();
  if (!workbookId) {
    return <Navigate to="/admin/sample-shipping" replace />;
  }

  const workbookPath = `/admin/sample-shipping/workbook/${workbookId}`;
  return (
    <ShipmentRequestTypeView
      dnaPath={`${workbookPath}/dna`}
      environmentalPath={`${workbookPath}/environmental`}
      backPath="/admin/sample-shipping"
    />
  );
}

function ShipmentRequestTypeView({
  dnaPath,
  environmentalPath,
  backPath,
}: {
  dnaPath: string;
  environmentalPath: string;
  backPath?: string;
}) {
  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar
        title="Request Shipment Pickup"
        $extraWide={true}
        controls={
          backPath ? (
            <Button as={RouterLink} to={backPath} variant="outline" textTransform="none">
              Back to shipments page
            </Button>
          ) : undefined
        }
      />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
          <Text color="gray.600" mb={4}>
            Choose the shipment type before continuing.
          </Text>
          <HStack spacing={4} align="stretch" flexDirection={{ base: 'column', md: 'row' }}>
            <ShipmentTypeCard
              to={dnaPath}
              title="DNA sample"
              description="Ship extracted environmental DNA in micro-centrifuge tubes."
            />
            <ShipmentTypeCard
              to={environmentalPath}
              title="Environmental sample"
              description="Ship unprocessed environmental samples for analysis."
              disabled={true}
              disabledLabel="Coming soon"
            />
          </HStack>
        </Box>
      </VStack>
    </div>
  );
}

export function AdminRawShipmentPlaceholderView() {
  return (
    <div style={{ marginBottom: theme.spacing[16] }}>
      <TitleBar title="Request Environmental Sample Pickup" $extraWide={true} />
      <VStack align="stretch" spacing={4} padding={theme.spacing[4]}>
        <Box bg="white" borderRadius={6} padding={theme.spacing[4]} boxShadow="sm">
          <Text fontWeight={600} mb={2}>
            Site under construction
          </Text>
          <Text color="gray.600" mb={4}>
            Environmental sample shipment requests are not available yet.
          </Text>
          <HStack spacing={3} align="stretch" flexDirection={{ base: 'column', md: 'row' }}>
            <Button as={RouterLink} to="/admin/shipment" variant="outline">
              Back
            </Button>
            <Button as={RouterLink} to="/admin/shipment/dna" colorScheme="blue">
              Go to DNA shipment form
            </Button>
          </HStack>
        </Box>
      </VStack>
    </div>
  );
}
